/**
 * Session Refresh Hook
 * Automatically refreshes session before expiry
 */

import { useEffect, useRef } from 'react';
import { useSession, useAuthStore } from '@/store/authStore';

// Session TTL from backend (24 hours in ms)
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

// Refresh when 20% of TTL remains
const REFRESH_THRESHOLD = 0.2;

// Minimum time between refresh attempts (5 minutes)
const MIN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface UseSessionRefreshOptions {
  enabled?: boolean;
  sessionTtlMs?: number;
  refreshThreshold?: number;
}

/**
 * Hook to automatically refresh session before expiry
 */
export function useSessionRefresh(options: UseSessionRefreshOptions = {}) {
  const {
    enabled = true,
    sessionTtlMs = SESSION_TTL_MS,
    refreshThreshold = REFRESH_THRESHOLD,
  } = options;

  const { sessionExpiresAt, refreshSession } = useSession();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled || !isAuthenticated || !sessionExpiresAt) {
      return;
    }

    const scheduleRefresh = () => {
      // Clear existing timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      const now = Date.now();
      const timeRemaining = sessionExpiresAt - now;
      const refreshThresholdMs = sessionTtlMs * refreshThreshold;

      // If less than threshold remains, refresh soon
      if (timeRemaining <= refreshThresholdMs) {
        // Don't refresh too frequently
        const timeSinceLastRefresh = now - lastRefreshRef.current;
        if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
          // Schedule for later
          const delay = MIN_REFRESH_INTERVAL_MS - timeSinceLastRefresh;
          refreshTimerRef.current = setTimeout(() => {
            lastRefreshRef.current = Date.now();
            refreshSession();
          }, delay);
        } else {
          // Refresh now
          lastRefreshRef.current = now;
          refreshSession();
        }
      } else {
        // Schedule refresh for when threshold is reached
        const delay = timeRemaining - refreshThresholdMs;
        refreshTimerRef.current = setTimeout(() => {
          lastRefreshRef.current = Date.now();
          refreshSession();
        }, delay);
      }
    };

    scheduleRefresh();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [enabled, isAuthenticated, sessionExpiresAt, sessionTtlMs, refreshThreshold, refreshSession]);

  // Handle tab focus - refresh if near expiry
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      return;
    }

    const handleFocus = () => {
      if (!sessionExpiresAt) return;

      const now = Date.now();
      const timeRemaining = sessionExpiresAt - now;
      const refreshThresholdMs = sessionTtlMs * refreshThreshold;

      // If near expiry when tab gains focus, refresh immediately
      if (timeRemaining <= refreshThresholdMs) {
        const timeSinceLastRefresh = now - lastRefreshRef.current;
        if (timeSinceLastRefresh >= MIN_REFRESH_INTERVAL_MS) {
          lastRefreshRef.current = now;
          refreshSession();
        }
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, isAuthenticated, sessionExpiresAt, sessionTtlMs, refreshThreshold, refreshSession]);

  return {
    sessionExpiresAt,
    refreshSession,
  };
}

export default useSessionRefresh;

